import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Save, Briefcase, ClipboardList, Target, Users, Wrench, Shield, FileDown } from "lucide-react";
import { SignatureCanvas } from "./SignatureCanvas";
import { exportJobDescriptionToPDF } from "@/lib/pdfExport";

interface JobDescriptionFormProps {
  employeeId: string;
  readOnly?: boolean;
}

interface JobDescriptionData {
  job_description?: string | null;
  position?: string;
  department?: string | null;
  job_description_signature_url?: string | null;
  job_description_signed_at?: string | null;
  job_description_signed_by?: string | null;
}

export function JobDescriptionForm({ employeeId, readOnly = false }: JobDescriptionFormProps) {
  const { user, profile, userRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employeeName, setEmployeeName] = useState("");
  const [position, setPosition] = useState("");
  const [department, setDepartment] = useState("");
  
  // Signature data
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [signedAt, setSignedAt] = useState<string | null>(null);
  const [signedByName, setSignedByName] = useState<string | null>(null);
  
  // Structured job description fields for craft industry
  const [mainTasks, setMainTasks] = useState("");
  const [responsibilities, setResponsibilities] = useState("");
  const [qualifications, setQualifications] = useState("");
  const [tools, setTools] = useState("");
  const [teamwork, setTeamwork] = useState("");
  const [safety, setSafety] = useState("");

  // Check if user is admin or hr_manager
  const isAdmin = userRole === "admin" || userRole === "hr_manager";

  useEffect(() => {
    if (user && employeeId) {
      loadData();
    }
  }, [user, employeeId]);

  const loadData = async () => {
    try {
      const { data, error } = await supabase
        .from("employees")
        .select("name, position, department, job_description, job_description_signature_url, job_description_signed_at, job_description_signed_by")
        .eq("id", employeeId)
        .single();

      if (error) throw error;

      setEmployeeName(data.name);
      setPosition(data.position || "");
      setDepartment(data.department || "");
      setSignatureUrl(data.job_description_signature_url);
      setSignedAt(data.job_description_signed_at);

      // If there's a signed_by user, fetch their name
      if (data.job_description_signed_by) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", data.job_description_signed_by)
          .single();
        
        if (profileData) {
          setSignedByName(profileData.full_name);
        }
      }

      // Parse existing job description if it exists
      if (data.job_description) {
        try {
          const parsed = JSON.parse(data.job_description);
          setMainTasks(parsed.mainTasks || "");
          setResponsibilities(parsed.responsibilities || "");
          setQualifications(parsed.qualifications || "");
          setTools(parsed.tools || "");
          setTeamwork(parsed.teamwork || "");
          setSafety(parsed.safety || "");
        } catch {
          // If not JSON, treat as plain text for backward compatibility
          setMainTasks(data.job_description);
        }
      }
    } catch (error) {
      console.error("Error loading job description:", error);
      toast.error("Fehler beim Laden der Stellenbeschreibung");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const jobDescriptionData = JSON.stringify({
        mainTasks,
        responsibilities,
        qualifications,
        tools,
        teamwork,
        safety
      });

      // Reset signature when job description content changes
      const { error } = await supabase
        .from("employees")
        .update({ 
          job_description: jobDescriptionData,
          job_description_signature_url: null,
          job_description_signed_at: null,
          job_description_signed_by: null
        })
        .eq("id", employeeId);

      if (error) throw error;

      // Reset local signature state
      setSignatureUrl(null);
      setSignedAt(null);
      setSignedByName(null);

      toast.success("Stellenbeschreibung gespeichert. Bitte unterschreiben Sie die aktualisierte Version.");
    } catch (error) {
      console.error("Error saving job description:", error);
      toast.error("Fehler beim Speichern der Stellenbeschreibung");
    } finally {
      setSaving(false);
    }
  };

  const handleSignature = async (signatureDataUrl: string) => {
    try {
      // Convert base64 to blob
      const response = await fetch(signatureDataUrl);
      const blob = await response.blob();
      
      // Create unique filename - use employee-profiles bucket which is public
      const filename = `${employeeId}/signature-${Date.now()}.png`;
      
      // Upload to storage (employee-profiles is public)
      const { error: uploadError } = await supabase.storage
        .from("employee-profiles")
        .upload(filename, blob, {
          contentType: "image/png",
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("employee-profiles")
        .getPublicUrl(filename);

      const publicUrl = urlData.publicUrl;
      const now = new Date().toISOString();

      // Update employee record with signature info - use employeeId as signed_by
      const { error: updateError } = await supabase
        .from("employees")
        .update({
          job_description_signature_url: publicUrl,
          job_description_signed_at: now,
          job_description_signed_by: employeeId // Use employee ID, not logged-in user
        })
        .eq("id", employeeId);

      if (updateError) throw updateError;

      // Update local state
      setSignatureUrl(publicUrl);
      setSignedAt(now);
      setSignedByName(employeeName); // Use employee name

      toast.success("Unterschrift erfolgreich gespeichert");
    } catch (error) {
      console.error("Error saving signature:", error);
      toast.error("Fehler beim Speichern der Unterschrift");
    }
  };

  const handleExportPDF = async () => {
    try {
      await exportJobDescriptionToPDF({
        employeeName,
        position,
        department,
        content: {
          mainTasks,
          responsibilities,
          qualifications,
          tools,
          teamwork,
          safety
        },
        signatureUrl,
        signedAt,
        signedByName
      }, {
        createdByName: profile?.full_name || undefined
      });
      toast.success("PDF erfolgreich exportiert");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Fehler beim PDF-Export");
    }
  };

  const handleDeleteSignature = async () => {
    try {
      const { error } = await supabase
        .from("employees")
        .update({
          job_description_signature_url: null,
          job_description_signed_at: null,
          job_description_signed_by: null
        })
        .eq("id", employeeId);

      if (error) throw error;

      setSignatureUrl(null);
      setSignedAt(null);
      setSignedByName(null);

      toast.success("Unterschrift erfolgreich gelöscht");
    } catch (error) {
      console.error("Error deleting signature:", error);
      toast.error("Fehler beim Löschen der Unterschrift");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const hasContent = mainTasks || responsibilities || qualifications || tools || teamwork || safety;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5" />
          Stellenbeschreibung
        </CardTitle>
        <CardDescription>
          {employeeName} - {position} {department && `(${department})`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ClipboardList className="h-4 w-4" />
                Hauptaufgaben
              </CardTitle>
              <CardDescription className="text-xs">
                Tägliche Kernaufgaben und Arbeitsbereiche
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={mainTasks}
                onChange={(e) => setMainTasks(e.target.value)}
                placeholder="z.B. Durchführung von Elektroinstallationen, Wartungsarbeiten, Kundenberatung..."
                className="min-h-[120px]"
                disabled={readOnly}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="h-4 w-4" />
                Verantwortungsbereiche
              </CardTitle>
              <CardDescription className="text-xs">
                Zuständigkeiten und Entscheidungsbefugnisse
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={responsibilities}
                onChange={(e) => setResponsibilities(e.target.value)}
                placeholder="z.B. Projektleitung, Materialbestellung, Qualitätskontrolle, Azubi-Betreuung..."
                className="min-h-[120px]"
                disabled={readOnly}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Qualifikationen & Kenntnisse
              </CardTitle>
              <CardDescription className="text-xs">
                Erforderliche Ausbildung und Fähigkeiten
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={qualifications}
                onChange={(e) => setQualifications(e.target.value)}
                placeholder="z.B. Gesellenbrief, Meistertitel, Führerschein Klasse B, Schweißschein..."
                className="min-h-[120px]"
                disabled={readOnly}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Wrench className="h-4 w-4" />
                Werkzeuge & Maschinen
              </CardTitle>
              <CardDescription className="text-xs">
                Regelmäßig verwendete Arbeitsmittel
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={tools}
                onChange={(e) => setTools(e.target.value)}
                placeholder="z.B. Messgeräte, Elektrowerkzeuge, Firmenfahrzeug, spezielle Maschinen..."
                className="min-h-[120px]"
                disabled={readOnly}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Zusammenarbeit
              </CardTitle>
              <CardDescription className="text-xs">
                Schnittstellen und Teamarbeit
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={teamwork}
                onChange={(e) => setTeamwork(e.target.value)}
                placeholder="z.B. Zusammenarbeit mit Bauleitung, Kundenkontakt, Koordination mit anderen Gewerken..."
                className="min-h-[120px]"
                disabled={readOnly}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Arbeitssicherheit
              </CardTitle>
              <CardDescription className="text-xs">
                Sicherheitsvorschriften und PSA
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={safety}
                onChange={(e) => setSafety(e.target.value)}
                placeholder="z.B. Sicherheitsschuhe, Helm, Schutzbrille, besondere Unterweisungen..."
                className="min-h-[120px]"
                disabled={readOnly}
              />
            </CardContent>
          </Card>
        </div>

        {/* Save Button - only for non-readonly mode */}
        {!readOnly && (
          <div className="flex justify-end gap-2">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Stellenbeschreibung speichern
                </>
              )}
            </Button>
          </div>
        )}

        {/* PDF Export Button - visible when there's content and signature */}
        {hasContent && signatureUrl && (
          <div className="flex justify-end">
            <Button variant="outline" onClick={handleExportPDF}>
              <FileDown className="mr-2 h-4 w-4" />
              Als PDF exportieren
            </Button>
          </div>
        )}

        {/* Signature Section - always visible */}
        {hasContent && (
          <div className="pt-4 border-t">
            <SignatureCanvas
              onSave={handleSignature}
              onDelete={isAdmin ? handleDeleteSignature : undefined}
              existingSignature={signatureUrl}
              signedAt={signedAt}
              signedByName={signedByName}
              disabled={readOnly && !signatureUrl}
              isAdmin={isAdmin}
              title="Unterschrift"
              description="Bitte unterschreiben Sie zur Bestätigung der Stellenbeschreibung"
              confirmationText="Mit Ihrer Unterschrift bestätigen Sie die Kenntnisnahme der oben aufgeführten Stellenbeschreibung."
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
